<template>
  <div class="database-page">
    <div class="page-header">
      <h2 class="page-title">背景数据库</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">关键字</span>
        <el-input
          v-model="keyword"
          size="small"
          placeholder="数据集名称"
          clearable
          style="width: 160px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">过程类型</span>
        <el-select v-model="category" size="small" clearable placeholder="全部" style="width: 140px">
          <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">数据来源</span>
        <el-select v-model="source" size="small" clearable placeholder="全部" style="width: 140px">
          <el-option v-for="item in sources" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="数据集总数"
        :today="summary.totalDatasets.today"
        :annual="summary.totalDatasets.annual"
        :unit="summary.totalDatasets.unit"
        :annual-unit="summary.totalDatasets.unit"
        icon="el-icon-coin"
        color="#409EFF"
      />
      <stat-card
        title="本年度更新"
        :today="summary.updatedThisYear.today"
        :annual="summary.updatedThisYear.annual"
        :unit="summary.updatedThisYear.unit"
        :annual-unit="summary.updatedThisYear.unit"
        icon="el-icon-refresh"
        color="#E6A23C"
      />
      <stat-card
        title="引用次数"
        :today="summary.totalRefs.today"
        :annual="summary.totalRefs.annual"
        :unit="summary.totalRefs.unit"
        :annual-unit="summary.totalRefs.unit"
        icon="el-icon-link"
        color="#67C23A"
      />
    </div>

    <div class="table-section">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="背景数据集" name="dataset">
          <div class="table-toolbar">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openDatasetDialog()">
              新增数据集
            </el-button>
          </div>
          <el-table :data="datasets" border stripe size="small">
            <el-table-column prop="name" label="数据集名称" min-width="220" show-overflow-tooltip />
            <el-table-column prop="processType" label="过程类型" width="120" />
            <el-table-column prop="region" label="地理范围" width="100" />
            <el-table-column prop="unit" label="单位" width="80" />
            <el-table-column prop="footprint" label="碳足迹值 (kgCO2e)" width="150" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.footprint, 4) }}</template>
            </el-table-column>
            <el-table-column prop="source" label="数据来源" width="100" />
            <el-table-column prop="year" label="适用年份" width="100" align="center" />
            <el-table-column prop="refCount" label="引用次数" width="100" align="right" />
            <el-table-column label="操作" width="100" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" @click="openDatasetDialog(row)">编辑</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="单位过程" name="process">
          <el-table :data="unitProcesses" border stripe size="small">
            <el-table-column prop="name" label="过程名称" min-width="220" show-overflow-tooltip />
            <el-table-column prop="ioSummary" label="输入/输出摘要" min-width="280" show-overflow-tooltip />
            <el-table-column prop="dataset" label="所属数据集" width="160" />
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="GWP 值" name="gwp">
          <el-table :data="gwpValues" border stripe size="small">
            <el-table-column prop="gas" label="温室气体种类" width="140" />
            <el-table-column prop="gwp" label="GWP 值" width="120" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.gwp, 0) }}</template>
            </el-table-column>
            <el-table-column prop="version" label="版本" width="100" align="center" />
            <el-table-column prop="standard" label="参考标准" min-width="160" />
            <el-table-column prop="year" label="适用年份" width="100" align="center" />
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog
      :title="datasetDialogTitle"
      :visible.sync="datasetDialogVisible"
      width="520px"
      @close="resetDatasetForm"
    >
      <el-form :model="datasetForm" label-width="100px" size="small">
        <el-form-item label="数据集名称" required>
          <el-input v-model="datasetForm.name" placeholder="请输入数据集名称" />
        </el-form-item>
        <el-form-item label="过程类型" required>
          <el-select v-model="datasetForm.category" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="地理范围">
          <el-input v-model="datasetForm.region" placeholder="如 中国" />
        </el-form-item>
        <el-form-item label="单位" required>
          <el-input v-model="datasetForm.unit" placeholder="如 kg / tkm" />
        </el-form-item>
        <el-form-item label="碳足迹值" required>
          <el-input-number
            v-model="datasetForm.footprint"
            :precision="4"
            :step="0.0001"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="数据来源">
          <el-select v-model="datasetForm.source" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in sources" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="适用年份">
          <el-date-picker
            v-model="datasetForm.year"
            type="year"
            size="small"
            placeholder="选择年份"
            value-format="yyyy"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="datasetDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveDataset">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import { getDatabaseData } from '@/api/modules/carbonFootprint'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonFootprintDatabase',
  components: { FilterBar, StatCard },
  data() {
    return {
      keyword: '',
      category: '',
      source: '',
      categories: [],
      sources: [],
      summary: {
        totalDatasets: { today: 0, annual: 0, unit: '条' },
        updatedThisYear: { today: 0, annual: 0, unit: '条' },
        totalRefs: { today: 0, annual: 0, unit: '次' }
      },
      activeTab: 'dataset',
      datasets: [],
      allDatasets: [],
      unitProcesses: [],
      gwpValues: [],
      datasetDialogVisible: false,
      datasetDialogTitle: '新增数据集',
      datasetForm: {
        id: null,
        name: '',
        processType: '',
        category: '',
        region: '中国',
        unit: '',
        footprint: 0,
        source: '',
        year: '2025',
        refCount: 0
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getDatabaseData({
        keyword: this.keyword,
        category: this.category,
        source: this.source
      })
      const data = res.data
      this.categories = data.categories
      this.sources = data.sources
      this.summary = data.summary
      if (!this.allDatasets.length) {
        this.allDatasets = data.datasets.map(item => ({ ...item }))
      }
      this.applyFilters(data)
      this.unitProcesses = data.unitProcesses
      this.gwpValues = data.gwpValues
    },
    applyFilters(data) {
      let list = [...this.allDatasets]
      if (this.keyword) {
        list = list.filter(item => item.name.includes(this.keyword))
      }
      if (this.category) {
        list = list.filter(item => item.category === this.category)
      }
      if (this.source) {
        list = list.filter(item => item.source === this.source)
      }
      this.datasets = list.length ? list : data.datasets
    },
    openDatasetDialog(row) {
      if (row) {
        this.datasetDialogTitle = '编辑数据集'
        this.datasetForm = { ...row, year: String(row.year) }
      } else {
        this.datasetDialogTitle = '新增数据集'
        this.resetDatasetForm()
      }
      this.datasetDialogVisible = true
    },
    resetDatasetForm() {
      this.datasetForm = {
        id: null,
        name: '',
        processType: '',
        category: '',
        region: '中国',
        unit: '',
        footprint: 0,
        source: '',
        year: '2025',
        refCount: 0
      }
    },
    saveDataset() {
      const year = Number(this.datasetForm.year) || 2025
      const payload = {
        ...this.datasetForm,
        processType: this.datasetForm.category,
        year
      }
      if (this.datasetForm.id) {
        const idx = this.allDatasets.findIndex(d => d.id === this.datasetForm.id)
        if (idx >= 0) {
          this.$set(this.allDatasets, idx, payload)
        }
      } else {
        this.allDatasets.push({
          ...payload,
          id: Date.now(),
          refCount: 0
        })
      }
      this.datasets = [...this.allDatasets]
      this.datasetDialogVisible = false
      this.$message.success('保存成功')
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}
</style>
